import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import { fetchAuditLogsAction } from "../../../../actions/fetchAuditLogsAction";
import "./index.scss";

const ViewLogsModal = ({ isOpen, onClose, clientData }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const entityPrimaryId =
    clientData?.entity?.entityPrimaryId || clientData?.entity?.id;
  const entityId = clientData?.entity?.entityId || "";
  const entityName =
    clientData?.entity?.entityName || clientData?.entity?.EntityName || "";

  // Fetch logs when modal opens or page changes
  useEffect(() => {
    if (isOpen && entityPrimaryId) {
      loadLogs();
    }
  }, [isOpen, entityPrimaryId, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLogs = async () => {
    setLoading(true);
    try {
      const result = await fetchAuditLogsAction({
        entityPrimaryId,
        pageNo: currentPage,
        pageSize,
      });

      if (result.success) {
        setLogs(result.data || []);
        setTotalCount(result.totalCount || 0);
      } else {
        toast.error(result.message || "Failed to load audit logs");
        setLogs([]);
        setTotalCount(0);
      }
    } catch (error) {
      toast.error("Failed to load audit logs");
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPage(1);
    setLogs([]);
    setTotalCount(0);
    onClose();
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startEntry = (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalCount);

  // Format date to match the design
  const formatDate = (dateString) => {
    if (!dateString) return { date: "—", time: "" };
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return { date: `${day} ${month} ${year}`, time };
  };

  // Get badge color based on field category
  const getFieldBadgeClass = (columnName, tableName) => {
    const name = columnName?.toLowerCase() || "";
    const table = tableName?.toLowerCase() || "";

    if (table.includes("kyc") || name.includes("kyc")) {
      return "vl-badge--primary";
    }
    if (table.includes("gst")) {
      return "vl-badge--gst";
    }
    if (
      table.includes("contact") ||
      name.includes("contact") ||
      name.includes("email")
    ) {
      return "vl-badge--contact";
    }
    if (
      table.includes("bank") ||
      name.includes("bank") ||
      name.includes("account")
    ) {
      return "vl-badge--bank";
    }
    if (table.includes("entity") || name.includes("entity")) {
      return "vl-badge--entity";
    }
    return "vl-badge--default";
  };

  // Format field name for display
  const formatFieldName = (columnName) => {
    if (!columnName) return "—";
    if (columnName === "FULL ROW") return "Full Row";
    // Convert camelCase or PascalCase to Title Case with spaces
    return columnName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Get action type badge
  const getActionBadge = (actionType) => {
    const actions = {
      INSERT: { label: "Created", class: "vl-action--insert" },
      UPDATE: { label: "Updated", class: "vl-action--update" },
      DELETE: { label: "Deleted", class: "vl-action--delete" },
    };
    return (
      actions[actionType] || { label: actionType, class: "vl-action--default" }
    );
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Record History / Audit Trail"
      size="xlarge"
    >
      <div className="vl-container">
        {/* Header with entity info and stats */}
        <div className="vl-header-card">
          <div className="vl-header-card__main">
            <div className="vl-header-card__icon">📋</div>
            <div className="vl-header-card__info">
              <h3 className="vl-header-card__title">{entityName}</h3>
              <p className="vl-header-card__subtitle">
                Entity ID:{" "}
                <span className="vl-header-card__id">{entityId}</span>
              </p>
            </div>
          </div>
          <div className="vl-header-card__stats">
            <div className="vl-stat">
              <div className="vl-stat__value">{totalCount}</div>
              <div className="vl-stat__label">Total Changes</div>
            </div>
            <div className="vl-stat vl-stat--divider"></div>
            <div className="vl-stat">
              <div className="vl-stat__value">{totalPages}</div>
              <div className="vl-stat__label">Pages</div>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="vl-info-banner">
          <svg
            className="vl-info-banner__icon"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span className="vl-info-banner__text">
            Showing complete audit trail for all field changes. Click on any row
            for more details.
          </span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="vl-loading">
            <div className="vl-loading__spinner"></div>
            <p className="vl-loading__text">Loading audit logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="vl-empty">
            <div className="vl-empty__icon">📭</div>
            <h3 className="vl-empty__title">No Audit Logs Found</h3>
            <p className="vl-empty__text">
              There are no recorded changes for this entity yet.
            </p>
          </div>
        ) : (
          <>
            <div className="vl-table-container">
              <div className="vl-table-wrapper">
                <table className="vl-table">
                  <thead>
                    <tr>
                      <th style={{ width: "20%" }}>Field Name</th>
                      <th style={{ width: "20%" }}>Old Value</th>
                      <th style={{ width: "20%" }}>New Value</th>
                      <th style={{ width: "15%" }}>Action</th>
                      <th style={{ width: "15%" }}>Changed By</th>
                      <th style={{ width: "10%" }}>Changed At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, index) => {
                      const { date, time } = formatDate(log.modifiedDate);
                      const action = getActionBadge(log.actionType);
                      return (
                        <tr
                          key={log.auditId || index}
                          className="vl-table__row"
                        >
                          <td>
                            <div className="vl-field">
                              <span className="vl-field__name">
                                {formatFieldName(log.columnName)}
                              </span>
                              <span
                                className={`vl-badge ${getFieldBadgeClass(log.columnName, log.tableName)}`}
                              >
                                {log.tableName || "Details"}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="vl-value-cell">
                              <span className="vl-value vl-value--old">
                                {log.oldValue || "—"}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="vl-value-cell">
                              <span className="vl-value vl-value--new">
                                {log.newValue || "—"}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className={`vl-action-badge ${action.class}`}>
                              {action.label}
                            </span>
                          </td>
                          <td>
                            <div className="vl-user">
                              <div className="vl-user__avatar">
                                {(log.modifiedBy || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                              <div className="vl-user__name">
                                {log.modifiedBy || "Unknown"}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="vl-date">
                              <div className="vl-date__day">{date}</div>
                              <div className="vl-date__time">{time}</div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Enhanced Pagination */}
            <div className="vl-pagination">
              <div className="vl-pagination__info">
                <span className="vl-pagination__text">
                  Showing <strong>{startEntry}</strong> to{" "}
                  <strong>{endEntry}</strong> of <strong>{totalCount}</strong>{" "}
                  entries
                </span>
              </div>
              <div className="vl-pagination__controls">
                <button
                  className="vl-pagination__btn vl-pagination__btn--nav"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  title="First page"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M11 2L5 8l6 6V2z" />
                    <path d="M5 2v12" />
                  </svg>
                </button>
                <button
                  className="vl-pagination__btn vl-pagination__btn--nav"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous page"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M10 2L4 8l6 6V2z" />
                  </svg>
                </button>

                {getPageNumbers().map((page, idx) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="vl-pagination__ellipsis"
                    >
                      ⋯
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`vl-pagination__btn ${page === currentPage ? "vl-pagination__btn--active" : ""}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  className="vl-pagination__btn vl-pagination__btn--nav"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  title="Next page"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M6 2l6 6-6 6V2z" />
                  </svg>
                </button>
                <button
                  className="vl-pagination__btn vl-pagination__btn--nav"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  title="Last page"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M5 2l6 6-6 6V2z" />
                    <path d="M11 2v12" />
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ViewLogsModal;
